import {Component, ElementRef, NgZone, ViewChild} from '@angular/core';
import {Subscription} from "rxjs";
import {ChatUser, Maybe} from "../../../models/chat/chat.type";
import {ConfigService} from "../../../services/config.service";
import {ChatService} from "../../../services/chat.service";
import {ChatMessageModel} from "../../../models/chat/chatMessage.model";
import {TranslateService} from "@ngx-translate/core";
import {UserService} from "../../../services/user.service";
import {UserModel} from "../../../models/user.model";


@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent {

  messages: any[] = [];
  messageSubs?: Subscription;

  chatInput = "";

  chatAvailable: boolean = false;
  chatUser!: ChatUser;
  chatUserSubs?: Subscription;

  user: UserModel | null = null;
  userSubs? : Subscription;

  availableSubs?: Subscription;
  channelSubs?: Subscription;

  isTyping: boolean = false;
  typingSubs?: Subscription;

  @ViewChild('chatMessageWrapper') chatWrapper!: ElementRef; //for scrolling to bottom
  private shouldScrollToBottom = false;

  supportEmail:string;
  chatHistoryText: string | any = '';

  canSend: boolean = false;
  @ViewChild('chatInputField') chatInputField!: ElementRef<HTMLInputElement>;


  constructor(
    private configs: ConfigService,
    public chatService: ChatService,
    private translate: TranslateService,
    private userService: UserService,
    private zone: NgZone
  ) {
    this.supportEmail = this.configs.contactEmail;
  }

  ngOnInit() {
    //messages from service:
    this.messageSubs = this.chatService.messagesInWs.subscribe(messages => {
      this.messages = messages;
      this.shouldScrollToBottom = true;
    })

    this.userSubs?.unsubscribe();
    this.userSubs = this.userService.userObject.subscribe(u=>{
      this.user = u;
      // console.log(this.user)
    })

    //for Rival chat
    if (this.configs.chatSystem === 'rival'){
      // if user logged in, open chat window, if not initialize websocket;
      this.channelSubs?.unsubscribe();
      this.channelSubs = this.chatService.channel.subscribe(value =>{
        if (!value){
          //if the user is not logged in, there is no ws connection so we have to initialize
          if (!this.chatUser || this.chatUser.role === 'guest'){  //to wait for the ws to close if the guest user had the chat opened
              this.chatService.initialize(true);
          }
        }
      })

      this.chatService.canSend.subscribe(value =>{
        this.canSend = value
        if (this.canSend) {
          this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => this.chatInputField?.nativeElement.focus());
          });
        }

      });
    }

    this.availableSubs?.unsubscribe();
    this.availableSubs = this.chatService.available.subscribe((available:boolean | null)=>{
      // console.log(available)
      if (available){
        this.chatAvailable = true;
        const message: ChatMessageModel = {
          isMe: false,
          // user: {username: "Customer Service"},
          // message: "We are here to help."
          user: {username: this.translate.instant('CHAT.CS_NAME')},
          message: this.translate.instant('CHAT.MESSAGE_CS_AVAILABLE')
        }
        if (!this.messages.length){
          this.messages.push(message)
        }
      } else if (available === false) {
        // chat is unavailable
        this.chatAvailable = false;
        const message: ChatMessageModel = {
          isMe: false,
          // user: {username: "Customer Service"},
          // message: "We're not available right now. Please try again later."
          user: {username: this.translate.instant('CHAT.CS_NAME')},
          message: this.translate.instant('CHAT.MESSAGE_CS_NOT_AVAILABLE')
        }
        this.messages.push(message)
      } else if (this.configs.chatSystem !=='rival'){
        // until not-rival-type chat gets implemented - todo
        this.chatAvailable = false;
        this.messages = [];
        const message: ChatMessageModel = {
          isMe: false,
          // user: {username: "Customer Service"},
          // message: "We're not available right now. Please try again later."
          user: {username: this.translate.instant('CHAT.CS_NAME')},
          message: this.translate.instant('CHAT.MESSAGE_CS_NOT_AVAILABLE')
        }
        this.messages.push(message)
      }
      //else available is null = initial value
    })


    this.chatUserSubs?.unsubscribe();
    this.chatUserSubs = this.chatService.user.subscribe((user: Maybe<ChatUser>)=> {
      // console.log("recieved about user:", user)
      if (user){
        this.chatUser = user;
      }
    })


    this.typingSubs?.unsubscribe()
    this.typingSubs = this.chatService.typingInProgress.subscribe(isTyping => {
      this.isTyping = isTyping;
      this.shouldScrollToBottom = true;
    })

  }



  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottomInChat();
      this.shouldScrollToBottom = false;
    }
  }


  sendMessage(){
    if (this.chatAvailable && this.chatInput.trim() !== '' && this.canSend) {
      this.chatService.sendMessage(this.chatInput)
      //reset input field
      this.chatInput = "";
    }
  }



  onBracketButtonClick(){
    /** send an email with chatHistory to support
     * for logged-in users
     *  - chat history exists in LS (with same sessionId) → send it
     *  - no LS / LS unavailable → fallback to the current in-memory messages
     *  - no messages → open empty email
     *  for not logged-in users = guest user
     *  - disabled button + a clear explanation nearby
     *  TRANSLATED to user language
     */
    const canSendEmail = !!this.chatUser && this.chatUser.role !== 'guest' && this.chatUser.role !== 'csr';
    if (canSendEmail){
      //get history from localstorage
      const regex = /\[\/\[(.*?)\]\/\]/g; //texts inside [/[ ]/]

      try {
        const token = localStorage.getItem('sessionToken');
        if (token){
          // console.log(token)
          this.chatHistoryText = this.messages.filter(m=>m.sessionId === token).map(m => `${m.timestamp ? new Date(m.timestamp).toLocaleString() : undefined} ${m.user.name}(role:${m.user.role}): ${m.message}`).join("\n").replace(regex, "");
          // console.log(this.chatHistoryText);
          if(this.chatHistoryText.length){
            //send email (messages from the session)
            // const subject = 'New Ticket - sending issue to Support from chat';
            // const body = encodeURIComponent(`Chat history of the session:${"\n"}${this.chatHistoryText}`);
            const subject = this.translate.instant('CHAT.TICKET_EMAIL_SUBJECT');
            const body = encodeURIComponent(`${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITH_HISTORY')}${"\n"}${this.chatHistoryText}`);
            // window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
            const newTab = window.open(`mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
            if(!newTab){
              window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
            }
          }else {
            //no history in this session, send empty email
            // const subject = 'New Ticket - sending issue to Support from chat';
            // const body = encodeURIComponent(`No chat history was found for this session.${"\n"}Please describe your issue here:`);
            const subject = this.translate.instant('CHAT.TICKET_EMAIL_SUBJECT');
            const body = encodeURIComponent(`${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITHOUT_HISTORY')}${"\n"}${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITHOUT_HISTORY2')}`);
            // window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
            const newTab = window.open(`mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
            if(!newTab){
              window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
            }
          }
        }
      } catch (error) {
        console.warn('Error accessing localStorage:', error);
        // checking messages in app memory
        this.chatHistoryText = this.messages.map(m => `${m.timestamp ? new Date(m.timestamp).toLocaleString() : undefined} ${m.user.name ?? 'system'}(role:${m.user.role ?? 'system'}): ${m.message}`).join("\n").replace(regex, "");
        if(this.chatHistoryText.length){
          // const subject = 'New Ticket - sending issue to Support from chat';
          // const body = encodeURIComponent(`Chat history of the last 7 days - no LS :${"\n"}${this.chatHistoryText}`);
          const subject = this.translate.instant('CHAT.TICKET_EMAIL_SUBJECT');
          const body = encodeURIComponent(`${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITHOUT_LS')}${"\n"}${this.chatHistoryText}`);
          // window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
          const newTab = window.open(`mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
          if(!newTab){
            window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
          }
        } else {
          //LS unavailable, no history in memory for the last 7 days - no message in session is good as message
          // const subject = 'New Ticket - sending issue to Support from chat';
          // const body = encodeURIComponent(`No chat history was found for this session.${"\n"}Please describe your issue here:`);
          const subject = this.translate.instant('CHAT.TICKET_EMAIL_SUBJECT');
          const body = encodeURIComponent(`${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITHOUT_HISTORY')}${"\n"}${this.translate.instant('CHAT.TICKET_EMAIL_BODY_WITHOUT_HISTORY2')}`);
          // window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
          const newTab = window.open(`mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
          if(!newTab){
            window.location.href = `mailto:${this.supportEmail}?subject=${encodeURIComponent(subject)}&body=${body}`;
          }
        }
      }
    } else {
      //fallback, will never happen as the button is disabled
      console.error('no token, cant send history - login please')

    }


  }

  closeChat(): void {
    this.chatService.toggleChat(false);
  }

  scrollToBottomInChat(){
    try {
      this.chatWrapper.nativeElement.scrollTop = this.chatWrapper.nativeElement.scrollHeight;
    } catch(err) {
      // console.error('Scroll to bottom failed:', err);
    }
  }

  ngOnDestroy(){
    this.chatUserSubs?.unsubscribe();
    this.availableSubs?.unsubscribe();
    this.channelSubs?.unsubscribe();

    this.messageSubs?.unsubscribe();
    this.typingSubs?.unsubscribe();

    this.userSubs?.unsubscribe();

    //for Rival chat
    if (this.configs.chatSystem === 'rival'){
      //if user is in guest mode, closing the component should close the ws
      if (this.chatUser && this.chatUser.role && this.chatUser.role === 'guest'){
        this.chatService.closeWebSocket();
      }
    }

  }



}
